import { quat, type Quat } from '../math/quat'
import { vec3, type Vec3 } from '../math/vec3'
import { pose as poseOps, type Pose } from '../math/pose'
import { clamp } from '../math/index'
import type { CameraModel } from '../camera/model'
import { projectToNormalized, unprojectAtDepth } from '../camera/model'
import type { PoseFrame } from '../tracking/types'
import { HAND_LM, POSE_LM } from '../tracking/landmarks'
import type { HandMetric } from './hand'

export interface WristSolveResult {
  /**
   * Wrist pose: origin on the forearm axis just behind the wrist crease,
   * +Z along the forearm toward the ELBOW, +Y toward the back of the hand (dorsal).
   */
  pose: Pose
  /** 0..1 — how much the body-pose elbow contributed to the forearm axis. */
  forearmWeight: number
  /** Estimated wrist width (cm) from hand scale. */
  wristWidthCm: number
}

export interface WristSolverOptions {
  /** Distance behind the wrist landmark to seat the bangle/watch (cm, along the forearm). */
  seatCm: number
  minVisibility: number
  fullVisibility: number
}

export const WRIST_SOLVER_DEFAULTS: WristSolverOptions = { seatCm: 1.8, minVisibility: 0.4, fullVisibility: 0.8 }

const _fore: Vec3 = [0, 0, 0]
const _poseDir: Vec3 = [0, 0, 0]
const _e: Vec3 = [0, 0, 0]
const _w: Vec3 = [0, 0, 0]
const _up: Vec3 = [0, 0, 0]
const _side: Vec3 = [0, 0, 0]
const _rot: Quat = [0, 0, 0, 1]
const _off: Vec3 = [0, 0, 0]

/**
 * Bracelet / bangle / watch solver.
 * Forearm axis: hand-only fallback = opposite of the finger direction (accurate for a straight
 * wrist); fused with the body-pose elbow→wrist direction (visibility-gated) which stays correct
 * when the wrist bends. Watch dials sit on +Y (dorsal) — flip via spec rotation for palm-side wear.
 */
export function solveWrist(
  hand: HandMetric,
  cam: CameraModel,
  spec: { offset: Vec3; rotation: Vec3; scale: number },
  poseFrame?: PoseFrame | null,
  options: Partial<WristSolverOptions> = {},
  out?: WristSolveResult,
): WristSolveResult {
  const opts = { ...WRIST_SOLVER_DEFAULTS, ...options }
  const res: WristSolveResult = out ?? { pose: poseOps.create(), forearmWeight: 0, wristWidthCm: 5.5 }

  // fallback forearm direction: away from the fingers (hand frame -Y)
  quat.rotateVec3(_fore, hand.frame.rotation, [0, -1, 0])

  // body-pose refinement: 3-D elbow→wrist direction lifted at the hand's depth
  let w = 0
  if (poseFrame) {
    const wristIdx = hand.handedness === 'left' ? POSE_LM.LEFT_WRIST : POSE_LM.RIGHT_WRIST
    const elbowIdx = hand.handedness === 'left' ? POSE_LM.LEFT_ELBOW : POSE_LM.RIGHT_ELBOW
    const vis = poseFrame.visibility
    const v = vis ? Math.min(vis[wristIdx]!, vis[elbowIdx]!) : 1
    w = clamp((v - opts.minVisibility) / (opts.fullVisibility - opts.minVisibility), 0, 1)
    // sanity gate: the pose skeleton's wrist must actually be THIS hand's wrist. Pose models
    // hallucinate arms when only a hand is in frame; a mismatched wrist would spin the watch.
    if (w > 0) {
      const hw: Vec3 = [hand.points[HAND_LM.WRIST * 3]!, hand.points[HAND_LM.WRIST * 3 + 1]!, hand.points[HAND_LM.WRIST * 3 + 2]!]
      const uv: [number, number] = [0, 0]
      if (projectToNormalized(uv, cam, hw)) {
        const dx = (uv[0] - poseFrame.landmarks[wristIdx * 3]!) * cam.aspect
        const dy = uv[1] - poseFrame.landmarks[wristIdx * 3 + 1]!
        if (Math.hypot(dx, dy) > 0.08) w = 0
      } else {
        w = 0
      }
    }
    if (w > 0 && elbowIdx * 3 + 1 < poseFrame.landmarks.length) {
      // lift both joints at the hand's fitted depth: gives the forearm's image-plane direction
      // exactly; depth slope comes from the world z delta when packed, else stays in-plane.
      unprojectAtDepth(_w, cam, poseFrame.landmarks[wristIdx * 3]!, poseFrame.landmarks[wristIdx * 3 + 1]!, hand.depth)
      unprojectAtDepth(_e, cam, poseFrame.landmarks[elbowIdx * 3]!, poseFrame.landmarks[elbowIdx * 3 + 1]!, hand.depth)
      if (poseFrame.worldLandmarks) {
        const dz = (poseFrame.worldLandmarks[elbowIdx * 3 + 2]! - poseFrame.worldLandmarks[wristIdx * 3 + 2]!) * 100
        _e[2] += dz
      }
      vec3.sub(_poseDir, _e, _w)
      if (vec3.length(_poseDir) > 3) {
        vec3.normalize(_poseDir, _poseDir)
        vec3.lerp(_fore, _fore, _poseDir, w)
        vec3.normalize(_fore, _fore)
      } else {
        w = 0
      }
    }
  }
  res.forearmWeight = w

  // frame: +Z toward the elbow, +Y dorsal (hand back), +X completes
  quat.rotateVec3(_up, hand.frame.rotation, [0, 0, 1])
  vec3.scaleAndAdd(_up, _up, _fore, -vec3.dot(_up, _fore))
  vec3.normalize(_up, _up)
  vec3.cross(_side, _up, _fore)
  const m = new Float32Array(16)
  m[0] = _side[0]; m[1] = _side[1]; m[2] = _side[2]
  m[4] = _up[0]; m[5] = _up[1]; m[6] = _up[2]
  m[8] = _fore[0]; m[9] = _fore[1]; m[10] = _fore[2]
  m[15] = 1
  quat.fromMat4(res.pose.rotation, m)
  quat.fromEulerDeg(_rot, spec.rotation[0], spec.rotation[1], spec.rotation[2])
  quat.multiply(res.pose.rotation, res.pose.rotation, _rot)
  quat.normalize(res.pose.rotation, res.pose.rotation)

  // position: wrist landmark + seat along the forearm + user offset in the wrist frame
  const wl = HAND_LM.WRIST
  res.pose.position[0] = hand.points[wl * 3]!
  res.pose.position[1] = hand.points[wl * 3 + 1]!
  res.pose.position[2] = hand.points[wl * 3 + 2]!
  vec3.scaleAndAdd(res.pose.position, res.pose.position, _fore, opts.seatCm)
  quat.rotateVec3(_off, res.pose.rotation, spec.offset)
  vec3.add(res.pose.position, res.pose.position, _off)
  res.pose.scale = spec.scale
  res.wristWidthCm = clamp(hand.knuckleSpanCm * 0.72, 4.5, 7.5)
  return res
}
